import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import { ConditionFormFromGet } from '@/models/conditionForm';
import { useDefectTypesOrganization } from '@/models/defectType';
import { useDatabaseTranslation } from '@/services/i18n';

import { QuestionEditor } from './QuestionEditor';

export type QuestionInput = Endpoints['PATCH /condition-forms/:id']['body']['questions'][number];
export type QuestionType = QuestionInput['type'];

export type AnswerInput = QuestionInput['answers'][number];
export type SubQuestionInput = NonNullable<AnswerInput['subQuestion']>;
export type SubQuestionAnswerInput = SubQuestionInput['answers'][number];

export type DefectTypeOption = {
  id: string;
  name: string;
};

export const ConditionFormEditor = ({
  conditionForm,
  questions: questionsProp,
  onChange,
  showErrors,
}: {
  conditionForm?: ConditionFormFromGet;
  questions?: QuestionInput[];
  onChange: (updatedQuestions: QuestionInput[]) => void;
  showErrors: boolean;
}) => {
  const { _db } = useDatabaseTranslation();
  const { data: defectTypes = [] } = useDefectTypesOrganization();

  const questions = questionsProp ?? conditionForm?.questions.map(serializeQuestion) ?? [];

  const defectTypeOptions = defectTypes.map((defectType) => ({
    id: defectType.id,
    name: _db(defectType.name),
  }));

  const reorderItems = <T extends { order: number }>(items: T[]) => {
    return items.map((item, index) => ({ ...item, order: index + 1 }));
  };

  const addQuestion = () => {
    const newQuestion: QuestionInput = {
      title: { fr: '', en: '' },
      type: 'radio',
      order: questions.length + 1,
      answers: [],
    };

    onChange([...questions, newQuestion]);
  };

  const updateQuestion = (questionIndex: number, updates: Partial<QuestionInput>) => {
    const updatedQuestions = questions.map((question, index) =>
      index === questionIndex ? { ...question, ...updates } : question
    );

    onChange(updatedQuestions);
  };

  const deleteQuestion = (questionIndex: number) => {
    const filteredQuestions = questions.filter((_, index) => index !== questionIndex);

    onChange(reorderItems(filteredQuestions));
  };

  const addAnswer = (questionIndex: number) => {
    const question = questions[questionIndex];
    const newAnswer: AnswerInput = {
      text: { fr: '', en: '' },
      order: question.answers.length + 1,
      defects: [],
    };

    updateQuestion(questionIndex, {
      answers: [...question.answers, newAnswer],
    });
  };

  const updateAnswer = (
    questionIndex: number,
    answerIndex: number,
    updates: Partial<AnswerInput>
  ) => {
    const question = questions[questionIndex];
    const updatedAnswers = question.answers.map((answer, index) =>
      index === answerIndex ? { ...answer, ...updates } : answer
    );

    updateQuestion(questionIndex, { answers: updatedAnswers });
  };

  const deleteAnswer = (questionIndex: number, answerIndex: number) => {
    const question = questions[questionIndex];
    const filteredAnswers = question.answers.filter((_, index) => index !== answerIndex);

    updateQuestion(questionIndex, { answers: reorderItems(filteredAnswers) });
  };

  const addSubQuestion = (questionIndex: number, answerIndex: number) => {
    updateAnswer(questionIndex, answerIndex, {
      subQuestion: {
        title: { fr: '', en: '' },
        type: 'radio' as QuestionType,
        order: 1,
        answers: [],
      },
    });
  };

  const deleteSubQuestion = (questionIndex: number, answerIndex: number) => {
    updateAnswer(questionIndex, answerIndex, { subQuestion: null });
  };

  const updateSubQuestion = (
    questionIndex: number,
    answerIndex: number,
    updates: Partial<SubQuestionInput>
  ) => {
    const answer = questions[questionIndex].answers[answerIndex];

    if (!answer.subQuestion) return;
    updateAnswer(questionIndex, answerIndex, {
      subQuestion: { ...answer.subQuestion, ...updates },
    });
  };

  const addSubQuestionAnswer = (questionIndex: number, answerIndex: number) => {
    const subQuestion = questions[questionIndex].answers[answerIndex].subQuestion;

    if (!subQuestion) return;
    const newAnswer: SubQuestionAnswerInput = {
      text: { fr: '', en: '' },
      order: subQuestion.answers.length + 1,
      defects: [],
    };

    updateSubQuestion(questionIndex, answerIndex, {
      answers: [...subQuestion.answers, newAnswer],
    });
  };

  const updateSubQuestionAnswer = (
    questionIndex: number,
    answerIndex: number,
    subAnswerIndex: number,
    updates: Partial<SubQuestionAnswerInput>
  ) => {
    const subQuestion = questions[questionIndex].answers[answerIndex].subQuestion;

    if (!subQuestion) return;
    const updatedAnswers = subQuestion.answers.map((sa, index) =>
      index === subAnswerIndex ? { ...sa, ...updates } : sa
    );

    updateSubQuestion(questionIndex, answerIndex, { answers: updatedAnswers });
  };

  const deleteSubQuestionAnswer = (
    questionIndex: number,
    answerIndex: number,
    subAnswerIndex: number
  ) => {
    const subQuestion = questions[questionIndex].answers[answerIndex].subQuestion;

    if (!subQuestion) return;
    const filteredAnswers = subQuestion.answers.filter((_, index) => index !== subAnswerIndex);

    updateSubQuestion(questionIndex, answerIndex, { answers: reorderItems(filteredAnswers) });
  };

  return (
    <Stack gap="2rem">
      {questions.length === 0 && (
        <Paragraph size="200" color="secondary">
          <Trans id="settings.condition-forms.no-questions">
            No questions yet. Add at least one question.
          </Trans>
        </Paragraph>
      )}

      {questions.map((question, questionIndex) => (
        <QuestionEditor
          key={question.id ?? questionIndex}
          question={question}
          questionIndex={questionIndex}
          onUpdate={(updates) => updateQuestion(questionIndex, updates)}
          onDelete={() => deleteQuestion(questionIndex)}
          onAddAnswer={() => addAnswer(questionIndex)}
          onUpdateAnswer={(answerIndex, updates) =>
            updateAnswer(questionIndex, answerIndex, updates)
          }
          onDeleteAnswer={(answerIndex) => deleteAnswer(questionIndex, answerIndex)}
          onAddSubQuestion={(answerIndex) => addSubQuestion(questionIndex, answerIndex)}
          onDeleteSubQuestion={(answerIndex) => deleteSubQuestion(questionIndex, answerIndex)}
          onUpdateSubQuestion={(answerIndex, updates) =>
            updateSubQuestion(questionIndex, answerIndex, updates)
          }
          onAddSubQuestionAnswer={(answerIndex) => addSubQuestionAnswer(questionIndex, answerIndex)}
          onUpdateSubQuestionAnswer={(answerIndex, subAnswerIndex, updates) =>
            updateSubQuestionAnswer(questionIndex, answerIndex, subAnswerIndex, updates)
          }
          onDeleteSubQuestionAnswer={(answerIndex, subAnswerIndex) =>
            deleteSubQuestionAnswer(questionIndex, answerIndex, subAnswerIndex)
          }
          defectTypeOptions={defectTypeOptions}
          showErrors={showErrors}
        />
      ))}

      <Button variant="primary" onPress={addQuestion}>
        <IconAdd />
        <Trans id="settings.condition-forms.add-question">Add question</Trans>
      </Button>
    </Stack>
  );
};

const serializeQuestion = (
  question: NonNullable<ConditionFormFromGet['questions'][number]>
): QuestionInput =>
  ({
    ...question,
    brands: question.brands?.map((brand: { id: string }) => brand.id),
    answers: question.answers?.map((answer) => ({
      ...answer,
      subQuestion: answer.subQuestion
        ? {
            ...answer.subQuestion,
            brands: answer.subQuestion.brands?.map((brand: { id: string }) => brand.id),
          }
        : answer.subQuestion,
    })),
  }) as QuestionInput;

export const useQuestionTypeOptions = () => {
  const { t } = useLingui();

  return [
    {
      value: 'radio' as const,
      label: t({
        id: 'settings.condition-forms.radio',
        message: 'Single choice',
      }),
    },
    {
      value: 'checkbox' as const,
      label: t({
        id: 'settings.condition-forms.checkbox',
        message: 'Multiple choices',
      }),
    },
    {
      value: 'multi-quantifiable-select' as const,
      label: t({
        id: 'settings.condition-forms.multi-quantifiable-select',
        message: 'Options with quantity',
      }),
    },
    {
      value: 'text-and-select' as const,
      label: t({
        id: 'settings.condition-forms.text-and-select',
        message: 'Option with text',
      }),
    },
  ];
};
